import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IUserBillingContactModel } from '../../../core/interfaces/billing-contact.interface';
import { Injectable } from '@angular/core';
import { Utility } from '../../../core/utility';
import { ISelectItemModel, IUserSelectItemModel } from '../../../core/interfaces/select-item.interface';
import { UserService } from '../../../core/user.service';
import { EditBillingContactDialogComponent } from '../../../billing-contacts/edit-billing-contact-dialog/edit-billing-contact-dialog.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SectionBase } from './base.section';
import { EditAssessmentRequestModel } from '../../edit-assessment-request.model';
import { AppService } from '../../../core/app.service';
import { PsValidators } from '../../../core/validators/count.validator';
import { IEditUserModel } from '../../../core/interfaces/edit-user.interface';
import { IAddressModel } from 'app/core/interfaces/address.interface';

@Injectable()
export class BillingContactSection extends SectionBase {
	private _billingContacts: IUserBillingContactModel[];
	private _states: ISelectItemModel[];
	private _countries: ISelectItemModel[];
	private _modal: NgbModalRef;

	constructor(private fb: UntypedFormBuilder,
		private modalService: NgbModal,
		private userService: UserService,
		private appService: AppService) {
		super();
	}

	public get hasBillingContact() {
		const fg = this.formGroup.value
		if (fg != null && fg.billingContactId != null)
			return false;
		else
			return true;
	}

	public get billingContactControl(): UntypedFormControl {
		return this.formGroup.controls['billingContact'] as UntypedFormControl;
	}

	public get billingContacts(): IUserBillingContactModel[] {
		return this._billingContacts || [];
	}

	public get isInvalid() {
		const fg = this.formGroup.value;
		if (!this.formGroup.touched)
			return false;

		if (fg != null && fg.billingContact && fg.billingContact.billingContactId != null)
			return false;
		else
			return true;
	}

	public init({ assessmentRequest, states, countries }: { assessmentRequest: EditAssessmentRequestModel, states: ISelectItemModel[], countries: ISelectItemModel[] }) {
		super.setAssessmentRequest(assessmentRequest);
		this.formGroup = this.buildFormGroup();

		if (!states) {
			throw new Error('states must be specified');
		}

		this._states = states;

		if (!countries) {
			throw new Error('countries must be specified');
		}

		this._countries = countries;

		const billingContact = this.assessmentRequest.billingContactId
			? this.billingContacts.find((bc) => bc.billingContactId === this.assessmentRequest.billingContactId)
			: null;

		if (billingContact) {
			this.billingContactControl.setValue(billingContact);
		}

		if (this.assessmentRequest.requestedByUserId) {
			this.retrieveBillingContacts(this.assessmentRequest.requestedByUserId, false, this.assessmentRequest.billingContactId);
		}

		this.appService.onLoggingOut.subscribe(() => {
			if (!this._modal) {
				return;
			}
			this._modal.dismiss('logging out');
		});
	}

	public get formValue(): any {
		const values = this.formGroup.value;

		return {
			billingContactId: values.billingContact ? values.billingContact.billingContactId : null
		};
	}

	public onClickAddBillingContact(requestedByUser: IEditUserModel, orgUnitName: string) {
		this._modal = this.modalService.open(EditBillingContactDialogComponent, { size: 'lg' });

		const instance = this._modal.componentInstance as EditBillingContactDialogComponent;
		instance.user = requestedByUser;
		instance.states = this._states;
		instance.countries = this._countries;
		instance.orgUnitName = orgUnitName;
		instance.assessmentRequestId = this.assessmentRequest?.assessmentRequestId;
		//instance.onSelectCountry(instance.billingContact.address.countryId);
		instance.onUpdate.subscribe(() => {
			this.retrieveBillingContacts(requestedByUser.userId, true);
		});
	}

	public onClickEditBillingContact(requestedByUser: IEditUserModel) {
		this._modal = this.modalService.open(EditBillingContactDialogComponent, { size: 'lg' });

		const instance = this._modal.componentInstance as EditBillingContactDialogComponent;
		instance.user = requestedByUser;
		instance.states = this._states;
		instance.countries = this._countries;
		instance.billingContact = this.formGroup.value.billingContact as IUserBillingContactModel;
		instance.assessmentRequestId = this.assessmentRequest?.assessmentRequestId;
		instance.orgUnitName = this.assessmentRequest?.orgUnitName;

		instance.onUpdate.subscribe((updatedContact: IUserBillingContactModel) => {
			this.applyUpdatedBillingContact(updatedContact);
		});
	}

	public retrieveBillingContacts(userId: number, reset: boolean, initialBillingContactId: number = null) {
		this.userService.getBillingContacts(userId).subscribe((billingContacts: IUserBillingContactModel[]) => {
			billingContacts.forEach((bc) => {
				const name = Utility.buildDisplayName(bc.firstName, bc.lastName);
				const address1 = bc.address?.address1 ? bc.address.address1.trim() : '';
				bc.displayName = address1 ? `${name} - ${address1}` : name;

				if (bc.address) {
					bc.address.phone = bc.address.phoneMobile || bc.address.phoneWork || bc.address.phoneHome;

					const country = this._countries.find((c) => c.id === bc.address.countryId);
					bc.address.country = country ? country.name : null;

					if (country && country.id === 196) {
						const state = this._states.find((s) => s.id === bc.address.stateId);
						const stateName = state ? state.name : null;
						bc.address.cityStateZip = this.formatCityStateZip(bc.address.city, stateName, bc.address.zipCode);
					} else {
						bc.address.cityStateZip = this.formatCityStateZip(bc.address.city, bc.address.province, bc.address.zipCode);
					}
				}
			});

			const snapshotContact = this.buildSnapshotBillingContact();

			if (snapshotContact && initialBillingContactId) {
				const existingIndex = billingContacts.findIndex(bc => bc.billingContactId === initialBillingContactId);

				if (existingIndex >= 0) {
					billingContacts[existingIndex] = snapshotContact;
				} else {
					billingContacts.unshift(snapshotContact);
				}
			}

			if (!this.assessmentRequest.hasParent) {
				this.billingContactControl.enable();
			}

			this._billingContacts = billingContacts;

			if (initialBillingContactId && !this.assessmentRequest.isNew) {
				const billingContact = this.billingContacts.find((bc) => bc.billingContactId === initialBillingContactId);
				this.billingContactControl.setValue(billingContact || -1);
			} else if (reset) {
				let defaultBillingContact = billingContacts.find((bc) => bc.isDefault);

				if (!defaultBillingContact && billingContacts.length === 1) {
					defaultBillingContact = billingContacts[0];
				}

				if (defaultBillingContact) {
					this.billingContactControl.setValue(defaultBillingContact);
				} else {
					this.billingContactControl.setValue(-1);
					this.billingContactControl.setErrors({ incorrect: true });
				}
			} else if (billingContacts.length === 1) {
				this.billingContactControl.setValue(billingContacts[0]);
			} else {
				this.billingContactControl.setValue(-1);
				this.billingContactControl.setErrors({ incorrect: true });
			}
		});
	}

	protected buildFormGroup(): UntypedFormGroup {
		return this.fb.group({
			billingContact: [{ value: null }, PsValidators.hasBillingContact.bind(this)], // {value: null, disabled: this.assessmentRequest.isNew}
		});
	}

	private formatCityStateZip(city: string, state: string, zipCode: string): string {
		let cityStateZip = city;

		if (cityStateZip && state) {
			cityStateZip += ', ';
		}

		if (state) {
			cityStateZip += state;
		}

		if (cityStateZip && zipCode) {
			cityStateZip += ' ';
		}

		if (zipCode) {
			cityStateZip += zipCode;
		}

		return cityStateZip;
	}

	private buildSnapshotBillingContact(): IUserBillingContactModel | null {
		const snap = this.assessmentRequest?.invoiceSnapshot;
		const billingContactId = this.assessmentRequest?.billingContactId;

		if (!snap || !billingContactId) {
			return null;
		}

		const country = this._countries?.find(c => c.id === snap.countryId);
		const state = this._states?.find(s => s.id === snap.stateId);

		let cityStateZip = null;
		if (country?.id === 196) {
			cityStateZip = this.formatCityStateZip(
				snap.city,
				state ? state.name : null,
				snap.postalCode
			);
		} else {
			cityStateZip = this.formatCityStateZip(
				snap.city,
				snap.province,
				snap.postalCode
			);
		}

		const address: IAddressModel = {
			address1: snap.address1,
			address2: snap.address2,
			city: snap.city,
			stateId: snap.stateId,
			province: snap.province,
			countryId: snap.countryId,
			zipCode: snap.postalCode,
			phoneWork: snap.phoneWork,
			phoneMobile: snap.phoneMobile,
			phoneHome: snap.phoneHome,
			phoneWorkCountryCode: snap.phoneWorkCountryCode,
			phoneMobileCountryCode: snap.phoneMobileCountryCode,
			phone: snap.phoneMobile || snap.phoneWork || snap.phoneHome,
			country: country ? country.name : null,
			cityStateZip: cityStateZip
		} as IAddressModel;

		const name = Utility.buildDisplayName(snap.firstName, snap.lastName);
		const displayName = snap.address1 ? `${name} - ${snap.address1.trim()}` : name;

		return {
			billingContactId: billingContactId,
			firstName: snap.firstName,
			middleName: snap.middleName,
			lastName: snap.lastName,
			email: snap.email,
			orgUnitName: "",
			address: address,
			userId: this.assessmentRequest.requestedByUserId,
			isDefault: false,
			displayName: displayName,
			isSnapshot: true
		};
	}

	public applyUpdatedBillingContact(updatedContact: IUserBillingContactModel, selectAsDefault: boolean = false): void {
		if (!updatedContact) {
			return;
		}

		const snapshotContact = this.toDisplayBillingContact(updatedContact, true);

		if (!this._billingContacts) {
			this._billingContacts = [];
		}

		const existingIndex = this._billingContacts.findIndex(
			bc => bc.billingContactId === snapshotContact.billingContactId
		);

		if (existingIndex >= 0) {
			this._billingContacts[existingIndex] = snapshotContact;
		} else {
			this._billingContacts.unshift(snapshotContact);
		}

		if (this.assessmentRequest) {
			this.assessmentRequest.billingContactId = snapshotContact.billingContactId;
			this.assessmentRequest.invoiceSnapshot = {
				firstName: snapshotContact.firstName,
				middleName: snapshotContact.middleName,
				lastName: snapshotContact.lastName,
				email: snapshotContact.email,
				address1: snapshotContact.address?.address1,
				address2: snapshotContact.address?.address2,
				city: snapshotContact.address?.city,
				stateId: snapshotContact.address?.stateId,
				province: snapshotContact.address?.province,
				countryId: snapshotContact.address?.countryId,
				postalCode: snapshotContact.address?.zipCode,
				phoneWork: snapshotContact.address?.phoneWork,
				phoneMobile: snapshotContact.address?.phoneMobile,
				phoneHome: snapshotContact.address?.phoneHome,
				//phoneWorkCountryCode: snapshotContact.address?.phoneWorkCountryCode,
				//phoneMobileCountryCode: snapshotContact.address?.phoneMobileCountryCode
			};
		}

		this.billingContactControl.setValue(snapshotContact);
		this.billingContactControl.markAsDirty();
		this.billingContactControl.markAsTouched();
		this.formGroup.updateValueAndValidity();
	}

	private toDisplayBillingContact(
		contact: IUserBillingContactModel,
		isSnapshot: boolean = false
	): IUserBillingContactModel {
		const clone: IUserBillingContactModel = {
			...contact,
			isSnapshot: isSnapshot,
			address: contact.address ? { ...contact.address } : undefined
		};

		const name = Utility.buildDisplayName(clone.firstName, clone.lastName);
		const address1 = clone.address?.address1 ? clone.address.address1.trim() : '';
		clone.displayName = address1 ? `${name} - ${address1}` : name;

		if (clone.address) {
			clone.address.phone = clone.address.phoneMobile || clone.address.phoneWork || clone.address.phoneHome;

			const country = this._countries.find((c) => c.id === clone.address.countryId);
			clone.address.country = country ? country.name : null;

			if (country && country.id === 196) {
				const state = this._states.find((s) => s.id === clone.address.stateId);
				clone.address.cityStateZip = this.formatCityStateZip(
					clone.address.city,
					state ? state.name : null,
					clone.address.zipCode
				);
			} else {
				clone.address.cityStateZip = this.formatCityStateZip(
					clone.address.city,
					clone.address.province,
					clone.address.zipCode
				);
			}
		}

		return clone;
	}
}
